import React, { ComponentPropsWithRef, type JSX } from 'react';
import cx from 'classnames';
import { Input } from 'antd';
import { css } from '@emotion/react';

const { TextArea, Search, Password } = Input;

type PasswordProps = ComponentPropsWithRef<typeof Password> & { inputType?: 'password' };
type EmailProps = ComponentPropsWithRef<typeof Input> & { inputType?: 'email' };
type TextAreaProps = ComponentPropsWithRef<typeof TextArea> & { inputType?: 'textArea' };
type SearchProps = ComponentPropsWithRef<typeof Search> & { inputType?: 'search' };
type InputProps = ComponentPropsWithRef<typeof Input> & { inputType: undefined };

const inputStyles = css`
  input {
    height: 44px;
    padding: 0.375rem 0.75rem;
    border-radius: 3px;
    outline: none;
    border: 0;
    font-size: 14px;
    font-weight: 500;
    border: none;
    border-bottom: 1px solid #ddd;
    width: 100%;

    &::placeholder {
      /* Firefox, Chrome, Opera */
      color: #d3d3d3;
      opacity: 0.5;
      font-size: 14px;
      font-weight: 500;
    }

    &:-ms-input-placeholder {
      /* Microsoft Edge */
      color: #d3d3d3;
      opacity: 0.5;
      font-size: 14px;
      font-weight: 500;
    }

    &:disabled {
      cursor: not-allowed;
      background-color: #d3d3d3;
    }
  }
`;

type Props = PasswordProps | TextAreaProps | SearchProps | InputProps | EmailProps;

const isPassword = (props: Props): props is PasswordProps => props.inputType === 'password';
const isTextArea = (props: Props): props is TextAreaProps => props.inputType === 'textArea';
const isSearch = (props: Props): props is SearchProps => props.inputType === 'search';
const isEmail = (props: Props): props is EmailProps => props.inputType === 'email';
const isInput = (props: Props): props is InputProps => !!props.inputType;

const Inputs: {
  (props: PasswordProps): JSX.Element;
  (props: TextAreaProps): JSX.Element;
  (props: SearchProps): JSX.Element;
  (props: InputProps): JSX.Element;
  (props: EmailProps): JSX.Element;
} = ({ className, ...otherProps }: Props) => {
  const classNames = cx('input', className);

  const switchType = () => {
    if (isPassword(otherProps)) {
      return <Input.Password className={classNames} css={inputStyles} {...otherProps} />;
    }
    if (isTextArea(otherProps)) {
      return <TextArea className={classNames} rows={4} css={inputStyles} {...otherProps} />;
    }
    if (isEmail(otherProps)) {
      return <Input className={classNames} css={inputStyles} {...otherProps} type="email" />;
    }
    if (isSearch(otherProps)) {
      return <Search className={classNames} css={inputStyles} {...otherProps} />;
    }
    if (isInput(otherProps)) {
      return <Input className={classNames} css={inputStyles} {...otherProps} />;
    }

    return null;
  };

  return <>{switchType()}</>;
};

export default Inputs;
